import { Controller } from "@hotwired/stimulus";
import type { Chart as ChartJSChart } from "chart.js";
import ChartJS from "chart.js/auto";

const DIMMED_COLOR = "rgba(148, 163, 184, 0.35)";
const DIMMED_COLOURS = { backgroundColor: DIMMED_COLOR, borderColor: DIMMED_COLOR };

type DatasetColours = {
  backgroundColor?: unknown;
  borderColor?: unknown;
};
type HighlightableDataset = {
  label?: string;
  type?: string;
  backgroundColor?: unknown;
  borderColor?: unknown;
};
type HighlightableElement = {
  options?: DatasetColours;
};

export default class extends Controller {
  private readonly originalColours = new Map<number, DatasetColours>();

  focus(event: Event) {
    if (!(event.currentTarget instanceof HTMLElement)) {
      return;
    }

    const label = event.currentTarget.dataset.chartHighlightLabel;
    const chart = this.chart;

    if (!label || !chart) {
      return;
    }

    this.applyHighlight(chart, label);
  }

  clear() {
    const chart = this.chart;

    if (!chart) {
      return;
    }

    this.applyHighlight(chart);
    this.originalColours.clear();
  }

  private get chart(): ChartJSChart | undefined {
    const canvas = this.element.querySelector("canvas");

    if (!(canvas instanceof HTMLCanvasElement)) {
      return undefined;
    }

    return ChartJS.getChart(canvas);
  }

  private applyHighlight(chart: ChartJSChart, activeLabel?: string) {
    const coloursByDataset = chart.data.datasets.map((dataset, datasetIndex) => {
      const highlightableDataset = dataset as unknown as HighlightableDataset;
      const colours = this.coloursFor(highlightableDataset, datasetIndex, activeLabel);

      this.setColours(highlightableDataset, colours);

      return colours;
    });

    chart.update("none");
    coloursByDataset.forEach((colours, datasetIndex) =>
      this.setElementColours(chart, datasetIndex, colours),
    );
    chart.draw();
  }

  private coloursFor(dataset: HighlightableDataset, datasetIndex: number, activeLabel?: string) {
    if (!this.originalColours.has(datasetIndex)) {
      this.originalColours.set(datasetIndex, {
        backgroundColor: dataset.backgroundColor,
        borderColor: dataset.borderColor,
      });
    }

    if (activeLabel && dataset.label !== activeLabel && !this.isBackgroundDataset(dataset)) {
      return DIMMED_COLOURS;
    }

    return this.originalColours.get(datasetIndex) || DIMMED_COLOURS;
  }

  private isBackgroundDataset(dataset: HighlightableDataset) {
    return dataset.label?.endsWith(" Background") || false;
  }

  private setColours(target: DatasetColours, colours: DatasetColours) {
    target.backgroundColor = colours.backgroundColor;
    target.borderColor = colours.borderColor;
  }

  private setElementColours(chart: ChartJSChart, datasetIndex: number, colours: DatasetColours) {
    const meta = chart.getDatasetMeta(datasetIndex);
    const elements = [meta.dataset, ...meta.data].filter(Boolean);

    elements.forEach((element) => {
      const options = (element as HighlightableElement).options;

      if (options) {
        this.setColours(options, colours);
      }
    });
  }
}
